/**
 * Hook V2 — useApprenantFinance
 *
 * Phase wiring 2 : branche sur `GET /api/v1/v2/apprenants/:id/finance`.
 * Fallback mock si API indisponible.
 *
 * Source : Zoho Books invoices filtrées par email apprenant + workflow inféré.
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../../../lib/api.js';

export type FactureStatus =
  | 'brouillon'
  | 'envoyée'
  | 'partiellement_payée'
  | 'payée'
  | 'en_retard'
  | 'annulée';
export type RelanceUrgence = 'normale' | 'urgente' | 'critique';

export interface FactureItem {
  id: string;
  numero: string;
  date: string;
  echeance: string;
  montantHT: number;
  montantTTC: number;
  status: FactureStatus;
  montantEncaisse: number;
  booksUrl: string | null;
  retardJours: number | null;
}

export interface EncaissementItem {
  id: string;
  date: string;
  montant: number;
  moyen: 'virement' | 'chèque' | 'cb' | 'autre';
  factureRef: string | null;
  source: string;
}

export interface RelanceItem {
  factureNumero: string;
  echeanceDepassee: string;
  montant: number;
  retardJours: number;
  derniereRelance: string | null;
  urgence: RelanceUrgence;
}

export interface ApprenantFinance {
  apprenantId: string;
  workflow: 'CPF' | 'OPCO' | 'ST_ENTRANTE' | 'DIRECT';
  workflowLabel: string;
  montantTotalHT: number;
  montantEncaisseHT: number;
  resteDuHT: number;
  factures: FactureItem[];
  encaissements: EncaissementItem[];
  relances: RelanceItem[];
  prochaineFacturationDate: string | null;
  prochaineFacturationMontant: number | null;
  prochaineFacturationCondition: string | null;
}

const MARTINE_MOCK: ApprenantFinance = {
  apprenantId: 'demo-martine',
  workflow: 'CPF',
  workflowLabel: 'CPF — Caisse des Dépôts',
  montantTotalHT: 5100,
  montantEncaisseHT: 0,
  resteDuHT: 5100,
  factures: [],
  encaissements: [],
  relances: [],
  prochaineFacturationDate: '2026-06-30',
  prochaineFacturationMontant: 5100,
  prochaineFacturationCondition:
    "Émettre la facture à la Caisse des Dépôts dès dépôt de l'attestation sur EDOF",
};

async function fetchFinance(id: string): Promise<ApprenantFinance & { source: 'api' | 'mock' }> {
  try {
    const data = await api.get<ApprenantFinance>(`/api/v1/v2/apprenants/${id}/finance`);
    return { ...data, source: 'api' };
  } catch (err) {
    if (err instanceof ApiClientError && [401, 404, 501, 0].includes(err.status)) {
      return { ...MARTINE_MOCK, source: 'mock' };
    }
    return { ...MARTINE_MOCK, source: 'mock' };
  }
}

export function useApprenantFinance(id: string): {
  data: (ApprenantFinance & { source: 'api' | 'mock' }) | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ['v2', 'apprenant-finance', id],
    queryFn: () => fetchFinance(id),
    staleTime: 60_000,
  });
  return { data: data ?? null, isLoading, error: (error as Error | null) ?? null };
}
